import { XMarkIcon } from '@heroicons/react/20/solid';
import classNames from 'classnames';
import { useContext, useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import ReactPlayer from 'react-player';
import { useExitIntent } from 'use-exit-intent';
import useSettings from '@/hooks/useSettings';
import { CheckoutModeContext } from '../../contexts/CheckoutModeContext';
import useImagePreview from '../../hooks/useImagePreview';
import { CheckoutComponentExitPopupAttibutesType } from '../../types/builder';
import Button from '../common/Button';
import Modal from '../common/Modal';

const CheckoutComponentExitPopup: React.FC<CheckoutComponentExitPopupAttibutesType> = ({
  type,
  actionOnClick,
  offer,
  title,
  description,
  actionLabel,
  image,
  video,
  url,
  backgroundButtonColor,
  textButtonColor,
  coupon
}) => {
  const [actionUrl, setActionUrl] = useState('');

  const ref = useRef<HTMLDialogElement>(null);

  const { registerHandler } = useExitIntent();

  const { preview } = useContext(CheckoutModeContext);

  const [_, setSearchParams] = useSearchParams();

  const settings = useSettings();

  registerHandler({
    id: 'exit-intent',
    handler: () => {
      if (!preview) {
        ref.current?.showModal();
      }
    }
  });

  const { preview: imagePreview } = useImagePreview({
    image
  });

  const handleOnClick = () => {
    if (actionOnClick === 'close') {
      ref.current?.close();
      return;
    }

    if (actionOnClick === 'discount') {
      setSearchParams({ coupon });
      ref.current?.close();
      return;
    }

    document.getElementById('cakto-open-exit-popup')?.click();
  };

  useEffect(() => {
    if (actionOnClick === 'offer') {
      const offerUrl = `${window.location.protocol}//${window.location.host}/${offer}`;
      setActionUrl(offerUrl);
    }
    if (actionOnClick === 'redirect') {
      setActionUrl(url);
    }
  }, [actionOnClick, offer, url]);

  return (
    <Modal
      ref={ref}
      onClose={() => ref.current?.close()}
      className={classNames('w-[600px] max-w-full mx-auto self-center rounded-xl overflow-hidden', {
        'aspect-square': type === 'image'
      })}
    >
      <XMarkIcon
        color={settings.icon.color}
        className="w-6 h-6 text-gray-500 absolute top-4 right-4 cursor-pointer"
        onClick={() => ref.current?.close()}
      />
      {type === 'image' && (
        <div className="flex flex-col h-full justify-center items-center">
          <img
            className={classNames('object-cover object-center w-full', {
              'w-10/12 h-auto': !!imagePreview,
              'w-48 h-48': !imagePreview
            })}
            src={imagePreview || '/assets/placeholder.svg'}
            alt="exit popup"
          />
          <div className="w-10/12 absolute bottom-12">
            <Button
              style={{
                color: textButtonColor,
                backgroundColor: backgroundButtonColor
              }}
              size="md"
              onClick={handleOnClick}
            >
              {actionLabel}
            </Button>
          </div>
        </div>
      )}
      {type === 'video' && (
        <div className="flex flex-col p-5 pt-10 gap-5">
          <h1 className="text-2xl font-bold text-gray-800">{title}</h1>
          <div className="w-full aspect-video bg-gray-100 rounded-lg">
            <ReactPlayer width="100%" height="100%" url={video} controls playing />
          </div>
          <div>
            <Button
              size="md"
              onClick={handleOnClick}
              style={{
                color: textButtonColor,
                backgroundColor: backgroundButtonColor
              }}
            >
              {actionLabel}
            </Button>
          </div>
        </div>
      )}
      {type === 'imageAndText' && (
        <div className="flex flex-row">
          <div className="w-1/3 bg-gray-100 flex justify-center items-center">
            <img
              className={classNames('object-cover object-center', {
                'w-full h-full': !!imagePreview,
                'w-24 h-24': !imagePreview
              })}
              src={imagePreview || '/assets/placeholder.svg'}
              alt="exit popup"
            />
          </div>
          <div className="w-2/3 flex flex-col justify-start items-start gap-5 p-8">
            <h1 className="text-2xl font-bold text-gray-800">{title}</h1>
            <p className="text-gray-600 text-sm italic">{description}</p>
            <div className="mt-5 w-full">
              <Button
                size="md"
                onClick={handleOnClick}
                style={{
                  color: textButtonColor,
                  backgroundColor: backgroundButtonColor
                }}
              >
                {actionLabel}
              </Button>
            </div>
          </div>
        </div>
      )}
      <a
        style={{ display: 'none' }}
        id="cakto-open-exit-popup"
        href={actionUrl}
        target="_blank"
        rel="noreferrer"
      >
        link
      </a>
    </Modal>
  );
};

export default CheckoutComponentExitPopup;
